'use client'

import {
  CloseButton,
  Portal,
  Select,
  Spinner,
  createListCollection,
} from '@chakra-ui/react'

interface CustomSelectProps<T> {
  options: { label: string; value: T }[]
  onChange: (value: T[]) => void
  value: T[]
  width?: string
  borderRadius?: string
  bg?: string
  borderColor?: string
  size?: 'sm' | 'md' | 'lg' | 'xs'
  loading?: boolean
  disabled?: boolean
  placeholder?: string
  multiple?: boolean
  /** Shows an × in the trigger that resets the selection to empty. */
  clearable?: boolean
}
const CustomSelect = <T extends string>({
  options,
  onChange,
  value,
  width = '100px',
  borderRadius = 'md',
  bg = 'bg.surface',
  borderColor = 'border',
  size = 'sm',
  loading = false,
  disabled = false,
  placeholder,
  multiple = false,
  clearable = false,
}: CustomSelectProps<T>) => {
  const collection = createListCollection({
    items: options,
  })
  return (
    <Select.Root
      collection={collection}
      width={width}
      value={value}
      size={size}
      multiple={multiple}
      onValueChange={(e) => onChange(e.value as T[])}
    >
      <Select.HiddenSelect />
      <Select.Control>
        <Select.Trigger
          bg={bg}
          borderColor={borderColor}
          borderRadius={borderRadius}
          disabled={loading || disabled}
        >
          <Select.ValueText placeholder={placeholder} />
        </Select.Trigger>
        <Select.IndicatorGroup>
          {loading ? (
            <Spinner size="sm" color="primary.fg" />
          ) : (
            <>
              {clearable && value.length > 0 && !disabled && (
                <Select.ClearTrigger asChild>
                  <CloseButton
                    size="xs"
                    variant="plain"
                    focusVisibleRing="none"
                  />
                </Select.ClearTrigger>
              )}
              <Select.Indicator />
            </>
          )}
        </Select.IndicatorGroup>
      </Select.Control>
      <Portal>
        <Select.Positioner>
          <Select.Content bg={'bg.surface'}>
            {collection.items.map((framework) => (
              <Select.Item
                _hover={{ bg: 'bg.subtle' }}
                item={framework}
                cursor={'pointer'}
                key={framework.value}
              >
                {framework.label}
                <Select.ItemIndicator />
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  )
}

export { CustomSelect }
